import { useRef, useState, type ComponentProps, type RefObject } from "react";
import { Alert, Button } from "@softmax/design-system";
import { AccountPlayerDialog } from "@softmax/product-ux/account";
import { useBrowserSession } from "@softmax/web-features/session";
import type { PlayerResponse } from "@softmax/observatory-client/types";
import { usePlayerMutation } from "@/features/players/commands";
import { isUncertainWrite } from "../write-failure";

export type PlayerIntent =
  | { kind: "create" }
  | { kind: "rename"; player: PlayerResponse };

export function PlayerOperation({
  intent,
  finalFocusRef,
  refreshing,
  refreshError,
  onRefresh,
  onClose,
}: {
  intent: PlayerIntent;
  finalFocusRef: RefObject<HTMLElement | null>;
  refreshing: boolean;
  refreshError: string | undefined;
  onRefresh(): void;
  onClose(): void;
}) {
  const session = useBrowserSession();
  const authorized = session.kind === "authenticated";
  const [name, setName] = useState(
    intent.kind === "rename" ? intent.player.name : "",
  );
  const mutation = usePlayerMutation({ elevatedPrivileges: false });
  const submitted = useRef(false);
  let feedback: Pick<
    ComponentProps<typeof AccountPlayerDialog>,
    "error" | "recovery"
  > = {};

  if (mutation.error) {
    const uncertain = isUncertainWrite(mutation.error);
    feedback = {
      ...feedback,
      error: uncertain
        ? `The result is unknown. Refresh players before retrying. ${mutation.error.message}`
        : mutation.error.message,
    };

    if (uncertain) {
      feedback = { ...feedback, recovery: { refreshing } };

      if (refreshError)
        feedback = {
          ...feedback,
          recovery: { refreshing, message: `Refresh failed: ${refreshError}` },
        };
    }
  }

  return (
    <AccountPlayerDialog
      open
      disabled={!authorized}
      notice={
        !authorized && (
          <Alert>
            Verify your account to continue.{" "}
            <Button onClick={() => void session.refresh()}>
              Retry account
            </Button>
          </Alert>
        )
      }
      mode={intent.kind}
      {...feedback}
      onRefresh={onRefresh}
      name={name}
      pending={mutation.isPending}
      finalFocusRef={finalFocusRef}
      onNameChange={(next) => {
        setName(next);

        if (!mutation.error || !isUncertainWrite(mutation.error))
          mutation.reset();
      }}
      onClose={() => {
        if (!submitted.current && !mutation.isPending) onClose();
      }}
      onSubmit={() => {
        if (
          submitted.current ||
          mutation.isPending ||
          !authorized ||
          (mutation.error && isUncertainWrite(mutation.error)) ||
          !name.trim()
        )
          return;

        submitted.current = true;
        mutation.mutate(
          intent.kind === "create"
            ? { kind: "create", name: name.trim() }
            : { kind: "rename", player: intent.player, name: name.trim() },
          {
            onSuccess: onClose,
            onSettled: () => {
              submitted.current = false;
            },
          },
        );
      }}
    />
  );
}
